// src/components/messaging/MessageInput.tsx
import React, { useState, useRef, useEffect } from "react";
import { Input, Button, Space, message as antMessage, Tooltip } from "antd";
import { SendOutlined, PaperClipOutlined, CloseCircleOutlined, FileOutlined } from "@ant-design/icons";
import { TypingIndicatorManager } from "../../api/messaging";
import { formatFileSize } from "../../api/messaging/MessagesApi";
import "./MessagingPanel.css";
import type { UserUuid } from "../../types/identity";

const { TextArea } = Input;

interface MessageInputProps {
	conversationId: string;
	userId: UserUuid;
	onSendMessage: (content: string, files?: File[]) => Promise<void>;
	disabled?: boolean;
}

const MessageInput: React.FC<MessageInputProps> = ({ conversationId, userId, onSendMessage, disabled = false }) => {
	const [message, setMessage] = useState("");
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [isSending, setIsSending] = useState(false);
	const typingManager = useRef<TypingIndicatorManager | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		typingManager.current = new TypingIndicatorManager(conversationId, userId);

		return () => {
			typingManager.current?.destroy();
		};
	}, [conversationId, userId]);

	const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setMessage(e.target.value);
		if (e.target.value.trim()) {
			typingManager.current?.onTyping();
		} else {
			typingManager.current?.stop();
		}
	};

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files || []);

		const validFiles = files.filter((file) => {
			if (file.size > 10 * 1024 * 1024) {
				antMessage.error(`${file.name} is larger than 10MB`);
				return false;
			}
			return true;
		});

		const totalFiles = selectedFiles.length + validFiles.length;
		if (totalFiles > 5) {
			antMessage.error("Maximum 5 files per message");
			setSelectedFiles([...selectedFiles, ...validFiles].slice(0, 5));
		} else {
			setSelectedFiles([...selectedFiles, ...validFiles]);
		}

		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const handleRemoveFile = (index: number) => {
		setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
	};

	const handleSend = async () => {
		const trimmedMessage = message.trim();
		if ((!trimmedMessage && selectedFiles.length === 0) || isSending) return;

		setIsSending(true);
		try {
			await onSendMessage(trimmedMessage || " ", selectedFiles.length > 0 ? selectedFiles : undefined);
			setMessage("");
			setSelectedFiles([]);
			typingManager.current?.stop();
		} catch (error) {
			console.error("Failed to send message:", error);
		} finally {
			setIsSending(false);
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	return (
		<div className="message-input-container">
			{selectedFiles.length > 0 && (
				<div className="message-attachments-preview">
					{selectedFiles.map((file, index) => (
						<div key={index} className="attachment-preview-item">
							<FileOutlined className="attachment-preview-icon" />
							<div className="attachment-preview-info">
								<span className="attachment-preview-name">{file.name}</span>
								<span className="attachment-preview-size">{formatFileSize(file.size)}</span>
							</div>
							<Button type="text" size="small" icon={<CloseCircleOutlined />} onClick={() => handleRemoveFile(index)} className="attachment-preview-remove" />
						</div>
					))}
				</div>
			)}
			<Space.Compact className="message-input-wrapper">
				<input data-testid="messaging-file-input" ref={fileInputRef} type="file" multiple onChange={handleFileSelect} style={{ display: "none" }} accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar" />
				<Tooltip title="Attach files (max 5 files, 10MB each)">
					<Button data-testid="messaging-attach-btn" type="text" icon={<PaperClipOutlined />} onClick={() => fileInputRef.current?.click()} disabled={disabled || isSending || selectedFiles.length >= 5} className="message-attach-button" />
				</Tooltip>
				<TextArea data-testid="messaging-input" value={message} onChange={handleInputChange} onKeyPress={handleKeyPress} placeholder="Type a message..." autoSize={{ minRows: 1, maxRows: 4 }} disabled={disabled || isSending} className="message-input" />
				<Button data-testid="messaging-send-btn" type="primary" icon={<SendOutlined />} onClick={handleSend} loading={isSending} disabled={disabled || (!message.trim() && selectedFiles.length === 0)} className="message-send-button">
					Send
				</Button>
			</Space.Compact>
		</div>
	);
};

export default MessageInput;
